import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { getRetailer } from '@/api/services/retail-org/retail-org.client';
import { useAuth } from '@/hooks/useAuth';
import { useRetailers } from '@/domains/retail/negocios/api/useRetailOrg';
import { resolveRetailerScope } from '../lib/retailerScope';

interface RetailerOption {
    retailerId: string;
    name: string;
}

/**
 * Vista pura del selector, separada del hook para poder testearla sin QueryClient.
 */
export function RetailerSelectView({
    retailers,
    value,
    onChange,
}: {
    retailers: RetailerOption[];
    value: string;
    onChange: (id: string) => void;
}) {
    const { t } = useTranslation('ventas');
    return (
        <select
            aria-label={t('filtros.cadena')}
            className="h-7 w-full rounded-md border border-border bg-background px-2 text-[11.5px]"
            value={value}
            onChange={(e) => {
                onChange(e.target.value);
            }}
        >
            <option value="">{t('filtros.elegirCadena')}</option>
            {retailers.map((r) => (
                <option key={r.retailerId} value={r.retailerId}>
                    {r.name}
                </option>
            ))}
        </select>
    );
}

/** La cadena fija de un usuario acotado: rótulo, no control. No hay nada que elegir. */
function RetailerFijo({ retailerId }: { retailerId: string }) {
    // `retail.retailer.read` es LEVEL_1 —el mismo nivel del Admin VL— así que ESTE sí responde,
    // a diferencia del listado. Si fallara, se muestra el id: peor es una etiqueta vacía.
    const { data } = useQuery({
        queryKey: ['retail', 'retailer', retailerId],
        queryFn: () => getRetailer(retailerId),
        staleTime: 5 * 60 * 1000,
    });
    return (
        <p className="flex h-7 items-center rounded-md border border-transparent bg-muted/40 px-2 text-[11.5px] font-medium">
            {data?.name ?? retailerId}
        </p>
    );
}

/**
 * Selector de cadena. Solo es un selector cuando el usuario TIENE de dónde elegir.
 *
 * Un Admin VL es LEVEL_1 sobre su cadena: `GET /retailers` (que exige TENANT) le responde 403, así
 * que el dropdown quedaba vacío y la pantalla trabada pidiéndole algo imposible. Para él la cadena
 * se deriva del `scopeRef` de su rol y se muestra como rótulo.
 */
export function RetailerSelect({
    value,
    onChange,
}: {
    value: string;
    onChange: (id: string) => void;
}) {
    const { roleAssignments } = useAuth();
    const { fixedRetailerId, canChoose } = resolveRetailerScope(roleAssignments);

    if (fixedRetailerId) return <RetailerFijo retailerId={fixedRetailerId} />;
    if (!canChoose) {
        // Ni cadena fija ni permiso para listar: se dice, en vez de mostrar un control muerto.
        return <SinCadena />;
    }
    return <RetailerSelectConLista value={value} onChange={onChange} />;
}

function SinCadena() {
    const { t } = useTranslation('ventas');
    return (
        <p className="rounded-md bg-muted/40 px-2 py-1.5 text-[11px] text-muted-foreground">
            {t('filtros.sinCadenaAsignada', {
                defaultValue: 'Tu usuario no tiene una cadena asignada.',
            })}
        </p>
    );
}

/** Solo para quien abarca el silo (TENANT/PLATFORM): ahí `GET /retailers` sí responde. */
function RetailerSelectConLista({
    value,
    onChange,
}: {
    value: string;
    onChange: (id: string) => void;
}) {
    const { data } = useRetailers();
    return <RetailerSelectView retailers={data ?? []} value={value} onChange={onChange} />;
}
